<template>
  <ODialog
    ref="dialog"
    :title="$t('components.service.actions.delete.title')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.confirm'), type: 'confirm', variant: 'danger', callback: confirm },
    ]"
    :portal="portal"
  />
</template>

<script setup lang="ts">
import type { Service } from '~/_api/models/service'

interface Props {
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'error'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification, $bus } = useNuxtApp()

const dialog = ref()
const service = ref<Service | null>(null)

const info = computed(() => {
  return $i18n.t('components.service.actions.delete.info', {
    title: service.value?.title,
    id: service.value?.idService
  })
})

const open = (serviceItem: Service) => {
  service.value = serviceItem
  dialog.value.open()
}

const confirm = async () => {
  if (!service.value) return

  emit('update:loading', true)

  try {
    const response = await $api.service.delete(service.value)
    emit('update:loading', false)

    if (response.success) {
      $notification.toast({
        title: $i18n.t('components.service.actions.delete.success'),
        text: $i18n.t('components.service.actions.delete.successInfo', { title: service.value.title }),
      })

      emit('success')
      $bus.emit.deleted('Service', service.value)

      return true
    }
  } catch (e) {
    emit('update:loading', false)

    $notification.toast({
      title: $i18n.t('components.service.actions.delete.error'),
      text: $i18n.t('components.service.actions.delete.errorInfo', { title: service.value?.title }),
    })

    emit('error')
  }
}

defineExpose({
  open
})
</script>
